/**
 * HighlightableField - Custom TextField that triggers highlighting on focus
 * Based on requirements 2.1, 2.3, 3.1, 4.1, 4.4
 */

import React, { useCallback, useRef, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { styled } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTheme } from '@mui/material/styles';
import { useValidation } from '../../contexts/ValidationContext';
import { useHighlighting } from '../../contexts/HighlightingContext';
import { ExtractedField } from '../../types';
import StatusBadge from '../shared/StatusBadge';
import TransformationPreview from './TransformationPreview';
import { CONFIDENCE_LOW_THRESHOLD, CONFIDENCE_HIGH_THRESHOLD } from '../../config/validation';

// Get confidence-based colors
const getConfidenceColors = (confidence: number, theme: any) => {
  if (confidence < CONFIDENCE_LOW_THRESHOLD) {
    return {
      borderColor: theme.palette.error.main,
      backgroundColor: theme.palette.error.light + '10',
      helperColor: theme.palette.error.main,
    };
  } else if (confidence < CONFIDENCE_HIGH_THRESHOLD) {
    return {
      borderColor: theme.palette.warning.main,
      backgroundColor: theme.palette.warning.light + '10',
      helperColor: theme.palette.warning.main,
    };
  } else {
    return {
      borderColor: theme.palette.success.main,
      backgroundColor: theme.palette.success.light + '10',
      helperColor: theme.palette.success.main,
    };
  }
};

// Styled TextField with confidence-based color coding
const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'confidence' && prop !== 'isHighlighted',
})<{ confidence?: number; isHighlighted?: boolean }>(({ theme, confidence = 100, isHighlighted }) => {
  const colors = getConfidenceColors(confidence, theme);
  
  return {
    '& .MuiOutlinedInput-root': {
      backgroundColor: colors.backgroundColor,
      transition: theme.transitions.create(['border-color', 'box-shadow'], {
        duration: theme.transitions.duration.short,
      }),
      '& input': {
        caretColor: theme.palette.text.primary,
      },
      '& fieldset': {
        borderColor: colors.borderColor,
        borderWidth: 2,
      },
      '&:hover fieldset': {
        borderColor: colors.borderColor,
        opacity: 0.8,
      },
      '&.Mui-focused fieldset': {
        borderColor: colors.borderColor,
        boxShadow: `0 0 0 2px ${colors.borderColor}25`,
      },
      ...(isHighlighted && {
        '& fieldset': {
          borderColor: theme.palette.primary.main,
          borderWidth: 2,
          boxShadow: `0 0 0 2px ${theme.palette.primary.main}25`,
        },
      }),
    },
    '& .MuiFormHelperText-root': {
      color: colors.helperColor,
      fontWeight: 500,
    },
  };
});

// Styled DatePicker with same confidence-based styling
const StyledDatePicker = styled(DatePicker, {
  shouldForwardProp: (prop) => prop !== 'confidence' && prop !== 'isHighlighted',
})<{ confidence?: number; isHighlighted?: boolean }>(({ theme, confidence = 100, isHighlighted }) => {
  const colors = getConfidenceColors(confidence, theme);
  
  return {
    '& .MuiOutlinedInput-root': {
      backgroundColor: colors.backgroundColor,
      transition: theme.transitions.create(['border-color', 'box-shadow'], {
        duration: theme.transitions.duration.short,
      }),
      '& input': {
        caretColor: theme.palette.text.primary,
      },
      '& fieldset': {
        borderColor: colors.borderColor,
        borderWidth: 2,
      },
      '&:hover fieldset': {
        borderColor: colors.borderColor,
        opacity: 0.8,
      },
      '&.Mui-focused fieldset': {
        borderColor: colors.borderColor,
        boxShadow: `0 0 0 2px ${colors.borderColor}25`,
      },
      ...(isHighlighted && {
        '& fieldset': {
          borderColor: theme.palette.primary.main,
          borderWidth: 2,
          boxShadow: `0 0 0 2px ${theme.palette.primary.main}25`,
        },
      }),
    },
  };
});

export interface HighlightableFieldProps {
  field: ExtractedField;
  value: string;
  onChange: (value: string) => void;
  onFocus?: (fieldId: string, boundingBoxes: ExtractedField['boundingBoxes']) => void;
  onBlur?: (fieldId: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
}

const HighlightableField: React.FC<HighlightableFieldProps> = ({
  field,
  value,
  onChange,
  onFocus,
  onBlur,
  disabled = false,
  autoFocus = false,
  className,
}) => {
  const theme = useTheme();
  const { state: validationState, actions: validationActions } = useValidation();
  const { state: highlightingState, actions: highlightingActions } = useHighlighting();
  const inputRef = useRef<HTMLInputElement>(null);

  // Check if this field is currently being highlighted
  const isHighlighted = highlightingState.activeFieldId === field.id;

  // Analyst confirmed (Enter) or edited this field this session (UX4)
  const isReviewed = !!validationState.reviewedFields?.[field.id];

  // Function to scroll PDF viewer to show the highlight
  const scrollToField = useCallback((boundingBox: any) => {
    if (!boundingBox) return;
    
    const pdfContainer = document.querySelector('[data-testid="pdf-viewer-container"]');
    if (!pdfContainer) return;
    
    // Get viewport from highlighting context
    const viewport = highlightingState.pdfViewport;
    if (!viewport) return;
    
    // Calculate position
    const highlightY = boundingBox.y * viewport.height;
    const highlightX = boundingBox.x * viewport.width;
    
    // Scroll with padding
    const padding = 100;
    pdfContainer.scrollTo({
      top: Math.max(0, highlightY - padding),
      left: Math.max(0, highlightX - padding),
      behavior: 'smooth'
    });
  }, [highlightingState.pdfViewport]);
  
  // Get field error from validation context
  const fieldError = validationState.fieldErrors[field.id] || '';
  
  // Handle field focus - trigger highlighting and page navigation
  const handleFocus = useCallback(() => {
    // Navigate to page and set as active field
    if (field.boundingBoxes && field.boundingBoxes.length > 0) {
      // For multi-page fields, find the page with the largest bounding box area
      const targetPage = field.boundingBoxes.length === 1 
        ? field.boundingBoxes[0].page
        : field.boundingBoxes.reduce((bestPage, bbox) => {
            const currentArea = bbox.width * bbox.height;
            const bestArea = bestPage.width * bestPage.height;
            return currentArea > bestArea ? bbox : bestPage;
          }).page;
      
      const currentPage = highlightingState.currentPage;
      
      // Navigate to page if different
      if (targetPage !== currentPage) {
        console.log(`Navigating to page ${targetPage} for field ${field.name}`);
        highlightingActions.setCurrentPage(targetPage);
        
        // Set active field after page change with a delay to allow viewport update
        setTimeout(() => {
          highlightingActions.setActiveField(field.id, field.boundingBoxes);
          // Scroll to the field location
          setTimeout(() => {
            scrollToField(field.boundingBoxes[0]);
          }, 100);
        }, 200);
      } else {
        // Same page - set highlight immediately
        highlightingActions.setActiveField(field.id, field.boundingBoxes);
        
        // Scroll to the field location
        setTimeout(() => {
          scrollToField(field.boundingBoxes[0]);
        }, 100);
      }
    } else {
      // No bounding boxes - just set as active field
      highlightingActions.setActiveField(field.id, field.boundingBoxes);
    }
    
    // Call optional onFocus callback
    if (onFocus) {
      onFocus(field.id, field.boundingBoxes);
    }
  }, [field.id, field.boundingBoxes, field.name, highlightingActions, highlightingState.currentPage, onFocus, scrollToField]);



  // Handle field blur - validate but keep highlights
  const handleBlur = useCallback(() => {
    // Don't clear highlights - keep showing all fields
    // Just clear the active field ID
    highlightingActions.setActiveField('all-fields', []);
    
    // Validate field
    validationActions.validateField(field.id);
    
    // Call optional onBlur callback
    if (onBlur) {
      onBlur(field.id);
    }
  }, [field.id, highlightingActions, validationActions, onBlur]);

  // Handle value change
  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    onChange(newValue);
    
    // Update validation context
    validationActions.updateField(field.id, newValue);
  }, [field.id, onChange, validationActions]);

  // Handle date change for DatePicker
  const handleDateChange = useCallback((date: Date | null) => {
    const newValue = date ? date.toISOString().split('T')[0] : '';
    onChange(newValue);
    
    // Update validation context
    validationActions.updateField(field.id, newValue);
  }, [field.id, onChange, validationActions]);

  // Auto-focus if requested
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Format field label with required indicator (prefer the schema display name — Sprint M2)
  const fieldDisplayName = field.displayName || field.name;
  const fieldLabel = field.required ? `${fieldDisplayName} *` : fieldDisplayName;

  // Helper text: surface the schema format hint alongside confidence (Sprint M2).
  // Keep the format hint visible in the error state too — that's when the user needs it most.
  const confidenceText = `Confidence: ${field.confidence}%`;
  const helperInfo = field.format ? `Format: ${field.format} — ${confidenceText}` : confidenceText;
  const helperText = fieldError
    ? (field.format ? `${fieldError} (expected: ${field.format})` : fieldError)
    : helperInfo;

  // Determine input type based on field type
  const getInputType = (fieldType: string): string => {
    switch (fieldType) {
      case 'number':
      case 'currency':
        return 'text'; // Use text to allow custom validation
      default:
        return 'text';
    }
  };

  // Format placeholder based on field type
  const getPlaceholder = (fieldType: string): string => {
    switch (fieldType) {
      case 'currency':
        return '$0.00';
      case 'number':
        return 'Enter number';
      case 'date':
        return 'Select date';
      default:
        return `Enter ${field.name.toLowerCase()}`;
    }
  };

  // Parse date value for DatePicker without timezone issues
  const parseDate = (dateValue: string): Date | null => {
    if (!dateValue) return null;
    try {
      // Check for ISO datetime format (2025-10-01T00:00:00.000Z)
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(dateValue)) {
        const dateOnly = dateValue.split('T')[0]; // Extract just the date part
        const [year, month, day] = dateOnly.split('-').map(Number);
        return new Date(year, month - 1, day); // month is 0-indexed
      }
      // Check for ISO date format (YYYY-MM-DD)
      else if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        const [year, month, day] = dateValue.split('-').map(Number);
        return new Date(year, month - 1, day); // month is 0-indexed
      } else {
        // For other formats, use regular Date parsing
        const date = new Date(dateValue);
        return isNaN(date.getTime()) ? null : date;
      }
    } catch {
      return null;
    }
  };

  // Inline transformation preview (UX4): the input holds the normalized, SOR-bound value; when
  // the normalize pipeline changed the extracted value, show the original alongside for review.
  // Rendered INSIDE helperText so MUI's aria-describedby wiring reads it to screen readers.
  const helperContent = field.rawValue != null ? (
    <>
      {helperText}
      <TransformationPreview rawValue={field.rawValue} />
    </>
  ) : helperText;

  // Reviewed indicator — titleAccess keeps the icon in the accessibility tree (MUI SvgIcon
  // sets aria-hidden when it's absent); Tooltip covers sighted hover. Never color alone.
  const reviewedIndicator = isReviewed ? (
    <Tooltip title="Reviewed">
      <CheckCircleOutlineIcon
        color="success"
        fontSize="small"
        titleAccess="Reviewed"
        sx={{ mr: 0.5 }}
      />
    </Tooltip>
  ) : null;

  // Render date field with DatePicker
  if (field.fieldType === 'date') {
    return (
      <Box id={`field-${field.id}`} data-field-id={field.id} sx={{ mb: 2 }}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
          <StyledDatePicker
            label={fieldLabel}
            value={parseDate(value)}
            onChange={handleDateChange}
            disabled={disabled}
            confidence={field.confidence}
            isHighlighted={isHighlighted}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: 'outlined',
                size: 'small',
                error: !!fieldError,
                helperText: helperContent,
                onFocus: handleFocus,
                onBlur: handleBlur,
                placeholder: field.format || getPlaceholder(field.fieldType),
                InputProps: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {reviewedIndicator}
                      <StatusBadge type="confidence" confidence={field.confidence} />
                      {/* Decorative only — a focusable button here would be a dead tab stop */}
                      <CalendarTodayIcon
                        fontSize="small"
                        aria-hidden="true"
                        sx={{ ml: 0.5, color: 'action.active' }}
                      />
                    </InputAdornment>
                  ),
                },
                sx: {
                  '& .MuiInputLabel-root': {
                    fontWeight: field.required ? 600 : 400,
                  },
                  // Apply confidence-based styling to DatePicker TextField
                  ...(() => {
                    const colors = getConfidenceColors(field.confidence, theme);
                    return {
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: colors.backgroundColor,
                        '& input': {
                          caretColor: theme.palette.text.primary,
                        },
                        '& fieldset': {
                          borderColor: colors.borderColor,
                          borderWidth: 2,
                        },
                        '&:hover fieldset': {
                          borderColor: colors.borderColor,
                          opacity: 0.8,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: colors.borderColor,
                          boxShadow: `0 0 0 2px ${colors.borderColor}25`,
                        },
                      },
                      '& .MuiFormHelperText-root': {
                        color: colors.helperColor,
                        fontWeight: 500,
                      },
                    };
                  })()
                },
              },
            }}
            className={className}
          />
      </LocalizationProvider>
      </Box>
    );
  }

  // Render regular text field
  return (
      <Box id={`field-${field.id}`} data-field-id={field.id} sx={{ mb: 2 }}>
      <StyledTextField
        ref={inputRef}
        fullWidth
        label={fieldLabel}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        type={getInputType(field.fieldType)}
        placeholder={field.format || getPlaceholder(field.fieldType)}
        disabled={disabled}
        error={!!fieldError}
        helperText={helperContent}
        confidence={field.confidence}
        isHighlighted={isHighlighted}
        className={className}
        variant="outlined"
        size="small"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {reviewedIndicator}
              <StatusBadge type="confidence" confidence={field.confidence} />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiInputLabel-root': {
            fontWeight: field.required ? 600 : 400,
          },
        }}
      />
      </Box>
  );
};

export default HighlightableField;